import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, message } from 'antd';
import { isEmpty, get } from 'lodash';
import { useAtomValue } from 'jotai';
import { PlusOutlined } from '@ant-design/icons';
import { METRICS_DEFAULT_ORDER, INVERTED_METRIC_SWITCH_MAP } from '@utils/metrics';
import {
  combineWithMetricDeDuplication,
  excludeWithMetricDeDuplication,
} from '@utils/metrics/metricHelpers';
import kpiStore from '@globalStore/kpiStore';
import metricsStore from '@src/globalStore/metricsStore';
import { contextTypeAtom } from '@src/atoms/page';
import {
  METRIC_POPUP_TITLE,
  METRIC_POPUP_OK_TEXT,
  CURRENT_PRODUCT_MODE,
  isAppModule,
  PRODUCT_MODE_MAP,
} from '@utils/constants';
import useKPI from '@hooks/useKPI';
import useMetricsMeta from '@hooks/useMetricsMeta';
import { clearCache } from '@src/api/cacheAdapter/config';
import useMetricCategories from './hooks/useMetricCategories';
import { useExcludeMetricCategories } from './hooks/useExcludeCategories';
import { useSupportedMetrics } from './hooks/useSupportedMetrics';
import MetricPopupModal from './MetricPopupModal';
import EmpUseCaseModal from '@components/common/EmpWidget/EmpUseCaseModal';
import EmpGenericMetricModal from '@components/common/EmpWidget/EmpGenericMetricModal';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { getUseCaseLimitation } from '@src/components/common/Dashboard/UseCases/limitation';

/*
    Function to sort list/array based on sortOrder
    Action: Sorts the unordered list based on ordered list.
  */
const orderedSort = (unorderedList, orderedList) =>
  unorderedList.sort((a, b) => orderedList.indexOf(a) - orderedList.indexOf(b));

const MetricSelectorPopup = ({
  supportedMetrics,
  selectedMetrics,
  lockedMetrics = [], // array of metrics, to be checked and disabled
  buttonText = 'Metrics',
  onApply,
  excludeCategories = [],
  metricsCountLimit = 999,
}) => {
  const dispatch = useDispatch();
  const currentKpi = kpiStore((state) => state.currentKpi);
  const searchRef: any = useRef(null);
  const contextType = useAtomValue(contextTypeAtom);
  const { combinedMetricsMeta } = metricsStore.getState();
  const currentMetricsMeta = combinedMetricsMeta[contextType.toLowerCase()];
  const [selectedMetricList, setSelectedMetrics] = useState(selectedMetrics);
  const [tempKpi, setTempKpi] = useState<number>(currentKpi?.kpiID);
  const metricsOrder = METRICS_DEFAULT_ORDER[CURRENT_PRODUCT_MODE];
  const [isUseCaseModalOpen, setIsUseCaseModalOpen] = useState(false);
  const [isGenericMetricModalOpen, setIsGenericMetricModalOpen] = useState(false);
  const [useCaseId, setUseCaseId] = useState<number>();
  const [visible, setVisible] = useState(false);
  const { applyKpiMutation } = useKPI();
  const { forceUpdateUserDefinedMetricsMeta, isUserDefinedMetricsLoading } = useMetricsMeta();

  const { usecaseKey } = useSelector(activeDashboardSelector);
  supportedMetrics = useSupportedMetrics(supportedMetrics);

  const lockedMetricsSet = useMemo(() => {
    const metricSet = new Set();
    lockedMetrics.forEach((m) => {
      metricSet.add(m);
      if (m in INVERTED_METRIC_SWITCH_MAP) {
        metricSet.add(INVERTED_METRIC_SWITCH_MAP[m]);
      }
    });
    return metricSet;
  }, [lockedMetrics]);

  useEffect(() => {
    setTempKpi(currentKpi?.kpiID);
  }, [currentKpi]);

  const refetchUserDefinedMetrics = useCallback(() => {
    forceUpdateUserDefinedMetricsMeta();
  }, [dispatch]);

  const openPopup = () => {
    setVisible(true);

    // immediately calling this action causes a render issue during transition animation
    // making it jittery on dark mode
    setTimeout(() => {
      // focus on search box after a delay
      // ref init + visual feedback after popup animation
      if (searchRef.current) {
        searchRef.current.focus({
          cursor: 'start',
        });
      }
      // refresh template metrics
      if (isAppModule || contextType === PRODUCT_MODE_MAP.APP) {
        refetchUserDefinedMetrics();
      }
    }, 500);
  };

  const closePopup = () => {
    setVisible(false);
    setSelectedMetrics(selectedMetrics); // restore seletect metrics
    setTempKpi(currentKpi?.kpiID);
  };

  const applyMetrics = () => {
    if (selectedMetricList && selectedMetricList.length > 0) {
      setVisible(false);
      if (metricsOrder.length > 0) {
        orderedSort(selectedMetricList, metricsOrder);
      }
      onApply(selectedMetricList, tempKpi !== currentKpi?.kpiID);
    } else {
      message.warning('Please select a metric.');
    }
  };

  const onOk = () => {
    if (tempKpi !== currentKpi?.kpiID) {
      applyKpiMutation.mutate(tempKpi, {
        onSuccess: () => {
          clearCache(); // clear axios cache as threshold changed
          applyMetrics();
        },
        onError: () => {
          message.error('There was an error saving KPI. Please try again later.');
        },
      });
    } else {
      applyMetrics();
    }
  };

  // clear all selected metrics
  const onClear = () => {
    // do not remove currently selected metric on trends
    setSelectedMetrics(lockedMetrics);
  };

  const latestExcludeCategories = useExcludeMetricCategories(excludeCategories);
  const metricCategories = useMetricCategories({
    supportedMetrics,
    selectedMetricList,
    lockedMetricsSet,
    excludeCategories: latestExcludeCategories,
    allMetrics: currentMetricsMeta,
    disableTemplateMetric: get(
      getUseCaseLimitation(usecaseKey),
      ['metrics', 'disableTemplateMetric'],
      false,
    ),
  });

  useEffect(() => {
    setSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  const handleSuccess = useCallback(() => {
    refetchUserDefinedMetrics();
  }, [refetchUserDefinedMetrics]);

  // preventing the rendering if we don't have the dimention list
  if (isEmpty(supportedMetrics)) {
    return null;
  }

  const onChange = (item) => {
    let listOfMetrics: any[] = [];
    const metricName = item.target.value;
    listOfMetrics.push(...selectedMetricList);

    if (listOfMetrics.includes(metricName)) {
      // uncheck action
      listOfMetrics = listOfMetrics.filter((value) => value !== metricName);
    } else {
      // check action
      listOfMetrics.push(metricName);
    }
    setSelectedMetrics(listOfMetrics);
  };

  // category level checkbox action
  const onBulkChange = (checked, inputMetricList) => {
    let listOfMetrics: any[] = [];

    if (checked) {
      listOfMetrics = combineWithMetricDeDuplication(selectedMetricList, inputMetricList);
    } else {
      // do not uncheck selected metric
      const filteredMetrics = inputMetricList.filter((m) => !lockedMetricsSet.has(m));
      listOfMetrics = excludeWithMetricDeDuplication(selectedMetricList, filteredMetrics);
    }
    setSelectedMetrics(listOfMetrics);
  };

  const onActivate = (id: number) => {
    if (id) {
      setIsUseCaseModalOpen(true);
      setUseCaseId(id);
    }
  };

  const onCreate = () => {
    setIsGenericMetricModalOpen(true);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (selectedMetricList.length > metricsCountLimit) {
      message.warning(`You can select a maximum of ${metricsCountLimit} metrics.`);
      // TODO: slicing should consider both secondary and order by metric
      setSelectedMetrics(selectedMetricList.slice(0, metricsCountLimit));
    }
  }, [selectedMetricList, metricsCountLimit]);

  return (
    <>
      <Button onClick={openPopup} icon={<PlusOutlined />}>
        {buttonText}
      </Button>
      <EmpUseCaseModal
        id={useCaseId}
        open={isUseCaseModalOpen}
        onCancel={() => setIsUseCaseModalOpen(false)}
        activateMutateOptions={{
          onSuccess: () => {
            message.success(`Metrics activated. Use the Data Modeling dashboard for updates.`);
            handleSuccess();
            setIsUseCaseModalOpen(false);
          },
        }}
      />
      <EmpGenericMetricModal
        open={isGenericMetricModalOpen}
        onCancel={() => setIsGenericMetricModalOpen(false)}
        activateMutateOptions={{
          onSuccess: () => {
            message.success(`Metrics created. Use the Data Modeling dashboard for updates.`);
            handleSuccess();
            setIsGenericMetricModalOpen(false);
          },
        }}
      />

      <MetricPopupModal
        title={METRIC_POPUP_TITLE}
        visible={visible}
        onCancel={closePopup}
        onOk={onOk}
        okText={METRIC_POPUP_OK_TEXT}
        metricCategories={metricCategories}
        selectedMetricList={selectedMetricList}
        onChange={onChange}
        onClear={onClear}
        onBulkChange={onBulkChange}
        applyKpiMutation={applyKpiMutation}
        tempKpi={tempKpi}
        setTempKpi={setTempKpi}
        searchRef={searchRef}
        onActivate={onActivate}
        onCreate={onCreate}
        isLoading={isUserDefinedMetricsLoading}
      />
    </>
  );
};

export default MetricSelectorPopup;
